import { CheckIcon } from "@heroicons/react/16/solid"
import { useAccountData } from "@src/hooks/useAccountData"
import { useConfidentialMode } from "@src/providers/ConfidentialModeProvider"
import clsx from "clsx"
import Button from "./Button"
import { Dialog } from "./Dialog"

const ConfidentialSwapIntroModal = ({
  open,
  onClose,
}: {
  open: boolean
  onClose: () => void
}) => {
  const { confidentialMode, setConfidentialMode } = useConfidentialMode()
  const { holdings, privateHoldings } = useAccountData()

  const hasHoldings = holdings != null && holdings.length > 0
  const hasPrivateHoldings = privateHoldings.length > 0

  const activeStep =
    !hasHoldings && !hasPrivateHoldings
      ? 0
      : !hasPrivateHoldings
        ? 1
        : !confidentialMode
          ? 2
          : 3

  const steps = [
    {
      title: "Deposit to your Main account",
      description: "Fund your account with 85+ assets across 35+ networks.",
      button: {
        text: "Deposit",
        href: "/receive",
      },
    },
    {
      title: "Move to your Confidential account",
      description:
        "Move assets to your Confidential account so swaps stay private.",
      button: {
        text: "Move to Confidential",
        href: "/transfer?mode=shield",
      },
    },
    {
      title: "Turn on confidential mode",
      description: "Switch to Confidential view to access private swaps.",
      button: {
        text: "Enable confidential mode",
        onClick: () => setConfidentialMode(true),
      },
    },
    {
      title: "Swap privately",
      description: "You’re all set — start swapping assets privately.",
      button: {
        text: "Start swapping",
        href: "/swap",
      },
    },
  ]

  return (
    <Dialog open={open} onClose={onClose} title="Start swapping privately">
      <div className="mt-2">
        {steps.map((step, index) => {
          const isLast = index === steps.length - 1
          const isActive = index === activeStep
          const isCompleted = index < activeStep || (isLast && isActive)

          return (
            <div
              key={step.title}
              className={clsx(
                "group flex h-max flex-row items-start justify-start gap-3",
                {
                  "opacity-60": !isActive && !isCompleted,
                }
              )}
            >
              <div className="flex flex-col items-center self-stretch">
                <span
                  className={clsx(
                    "z-10 flex size-6 shrink-0 items-center justify-center rounded-full",
                    isCompleted
                      ? "bg-green-600 text-white dark:bg-green-500 dark:text-gray-900"
                      : "text-gray-900 ring-1 ring-gray-300 ring-inset dark:text-white dark:ring-gray-600"
                  )}
                >
                  {isCompleted ? (
                    <CheckIcon className="size-4" />
                  ) : (
                    <span className="font-semibold text-xs">{index + 1}</span>
                  )}
                </span>
                {!isLast && (
                  <div className="relative my-1 flex size-full justify-center self-center overflow-hidden">
                    <div className="absolute h-full border-gray-300 border-r-2 border-dotted dark:border-gray-600" />
                  </div>
                )}
              </div>
              <div className="flex flex-col items-start pt-0.5 not-group-last:pb-6">
                <p className="font-semibold text-gray-900 text-sm dark:text-white">
                  {step.title}
                </p>
                <p className="text-pretty font-medium text-gray-500 text-sm dark:text-gray-400">
                  {step.description}
                </p>
                {isActive && (
                  <Button
                    className="mt-2"
                    variant="primary"
                    size="sm"
                    href={step.button.href ?? undefined}
                    onClick={step.button.onClick ?? undefined}
                  >
                    {step.button.text}
                  </Button>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </Dialog>
  )
}

export default ConfidentialSwapIntroModal
